import React, { useState } from 'react';
import styled from '@emotion/styled';
import { placeholder } from 'polished';

import { useSession, useTyping } from '../context';
import { sendNewCustomerMessage } from '../services/sendNewCustomerMessage';

interface ChatInputProps {
  showYesNo: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ showYesNo }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const { conversation } = useSession();
  const { setIsTyping, isTyping } = useTyping();

  async function submitMessage(e: React.SyntheticEvent, inputValue: string) {
    e.preventDefault();
    const messageToSend = inputValue;
    setInputValue('');

    // Only create a typing gif when bot will respond (i.e. NOT web chat)
    if (!conversation.value.useHumanAgent) {
      setIsTyping(true);
    }

    const { success } = await sendNewCustomerMessage({
      conversationId: conversation.ref.id,
      text: inputValue,
    });

    setIsTyping(false);

    if (!success) {
      // reset input to customer message to try again
      return setInputValue(messageToSend);
    }
  }

  return (
    <div
      css={{
        padding: '0 15px',
        width: '100%',
        backgroundColor: 'white',
        // boxShadow: showYesNo ? "none" : "0px -5px 15px -12px black",
        zIndex: 2,
      }}
    >
      <TypingText show={isTyping}>Chatbot is thinking...</TypingText>
      <div css={{ padding: '0px 0px 16px 0px' }}>
        {showYesNo ? (
          <ButtonContainer>
            <Button onClick={e => submitMessage(e, 'yes')}>Yes</Button>
            <Button onClick={e => submitMessage(e, 'no')}>No</Button>
          </ButtonContainer>
        ) : (
          <form
            action="#"
            onSubmit={e => {
              e.preventDefault();
              if (inputValue) submitMessage(e, inputValue);
            }}
          >
            <input
              css={(theme: Theme) => ({
                width: '100%',
                height: 36,
                backgroundColor: 'white',
                border: `1px solid ${theme.colors.grayLightOutline}`,
                borderRadius: 4,
                fontSize: 16,
                padding: '3px 10px',
                ...placeholder({
                  color: theme.colors.placeholderTextColor,
                }),
                '&:focus': {
                  outlineColor: theme.colors.primary,
                },
              })}
              autoComplete="off"
              aria-label="Send a message"
              tabIndex={1000}
              name="message"
              placeholder="Send a message"
              type="text"
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value);
              }}
            />
          </form>
        )}
      </div>
    </div>
  );
};

export default ChatInput;

/** YES or NO button when "speak_to_human" action occurs */
const Button = styled.button`
  padding: 5px 15px;
  border-radius: 10px;
  text-transform: uppercase;
  background-color: white;
  border: 1px solid rgb(0, 112, 255);
  color: rgb(0, 112, 255);
  font-size: 16;
  &:hover {
    background-color: rgb(0, 112, 255);
    color: white;
    cursor: pointer;
  }
`;

/** Container for "yes" and "no" buttons */
const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const TypingText = styled.div<{ show: boolean }>`
  color: ${({ show }) => (show ? 'gray' : 'white')};
  font-size: 12px;
  font-style: italic;
  padding: 4px;
`;
